import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import "@uiw/react-markdown-preview/markdown.css";
import { X, Save, Send, EyeOff, Eye, History, Plus, Trash2, Loader2, AlertTriangle, CheckCircle2, ShieldCheck, RotateCcw } from "lucide-react";
import { PostBody } from "@/components/PostBody";

/* ============================ Shared helpers ============================ */
export const POST_CATEGORIES = ["Visas", "Tax", "Healthcare", "Banking", "Lifestyle", "Property", "Legal"];
export const ROUTE_TAGS = ["general", "work", "retire", "home"];
const SENSITIVE_CATEGORIES = ["Visas", "Tax", "Healthcare", "Banking", "Property", "Legal"];
const SENSITIVE_RE = /visa|tax|health|bank|mortgage|property|legal|nie|residen|immigration/i;

export function slugify(v) {
  return (v || "").toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
}
export function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toISOString().slice(0, 10); } catch { return String(d).slice(0, 10); }
}
export function isSensitivePost(p) {
  return SENSITIVE_CATEGORIES.includes(p.category) || SENSITIVE_RE.test(`${p.title || ""} ${p.slug || ""} ${p.h1 || ""}`);
}
function hasInternalLink(body) {
  return /\]\(\/(blog|spain|resources|about|nie)/.test(body || "") || /\]\(https?:\/\/(www\.)?destinoeurope\.com/.test(body || "");
}

export function auditPost(p) {
  const errors = [], warnings = [];
  if (!p.title?.trim()) errors.push("Missing title");
  if (!p.slug?.trim()) errors.push("Missing slug");
  if (!p.body || p.body.trim().length < 20) errors.push("Body is empty or too short (min 20 chars)");
  if (!p.excerpt || p.excerpt.trim().length < 10) errors.push("Excerpt is too short (min 10 chars)");
  const t = (p.seo_title || p.title || "");
  if (!p.seo_title?.trim()) warnings.push("No SEO title (falls back to title)");
  if ((p.meta_description || "").length === 0) warnings.push("No meta description (falls back to excerpt)");
  else if (p.meta_description.length > 170) warnings.push("Meta description is long (>170 chars)");
  else if (p.meta_description.length < 80) warnings.push("Meta description is short (<80 chars)");
  if (t.length > 65) warnings.push("Title unusually long (>65 chars)");
  if (!(p.route_tags || []).length) warnings.push("No route classification");
  if (!hasInternalLink(p.body)) warnings.push("No internal links detected in body");
  if (!(p.sources || []).filter(Boolean).length) warnings.push("No sources");
  if (!p.last_reviewed) warnings.push("No last_reviewed date");
  if (p.editorial_status === "review" || p.editorial_status === "review_required") warnings.push(`Editorial status is ${p.editorial_status}`);
  return { errors, warnings };
}
export function auditSeo(p) {
  const errors = [], warnings = [];
  if (!p.h1?.trim()) errors.push("Missing H1");
  if (!p.slug?.trim()) errors.push("Missing slug");
  if (!p.meta_title?.trim()) errors.push("Missing meta title");
  if (!(p.sections || []).filter((s) => s.h2?.trim() && s.body?.trim()).length) errors.push("No sections with content");
  if ((p.meta_description || "").length === 0) warnings.push("No meta description");
  else if (p.meta_description.length > 170) warnings.push("Meta description is long (>170 chars)");
  else if (p.meta_description.length < 80) warnings.push("Meta description is short (<80 chars)");
  if ((p.meta_title || "").length > 65) warnings.push("Meta title unusually long (>65 chars)");
  if (!(p.faq || []).filter((f) => f.q?.trim() && f.a?.trim()).length) warnings.push("No FAQ entries");
  if (!(p.sources || []).filter(Boolean).length) warnings.push("No sources");
  if (!p.last_reviewed) warnings.push("No last_reviewed date");
  if (p.editorial_status !== "verified") warnings.push(`Editorial status is ${p.editorial_status || "unset"}`);
  return { errors, warnings };
}

/* ============================ Small UI atoms ============================ */
const labelCls = "block text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-2";
function Txt({ label, value, on, testid, placeholder, hint, disabled }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <input data-testid={testid} value={value || ""} disabled={disabled} placeholder={placeholder}
        onChange={(e) => on(e.target.value)}
        className="w-full bg-white border border-[#451C25]/20 px-3 py-2 text-sm focus:outline-none focus:border-[#451C25] disabled:opacity-60" />
      {hint && <p className="mt-1 text-[10px] text-[#6B4954]">{hint}</p>}
    </div>
  );
}
function Area({ label, value, on, testid, rows = 3, hint }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <textarea data-testid={testid} value={value || ""} rows={rows} onChange={(e) => on(e.target.value)}
        className="w-full bg-white border border-[#451C25]/20 px-3 py-2 text-sm focus:outline-none focus:border-[#451C25]" />
      {hint && <p className="mt-1 text-[10px] text-[#6B4954]">{hint}</p>}
    </div>
  );
}
function Sel({ label, value, options, on, testid }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <select data-testid={testid} value={value || ""} onChange={(e) => on(e.target.value)}
        className="w-full bg-white border border-[#451C25]/20 py-2 px-3 text-sm focus:outline-none focus:border-[#451C25]">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

export function SourcesEditor({ sources, onChange, testidPrefix = "cm-source" }) {
  const list = sources || [];
  return (
    <div data-testid={`${testidPrefix}s`}>
      <div className="flex items-center justify-between mb-2">
        <label className={labelCls} style={{ marginBottom: 0 }}>Sources (official URLs)</label>
        <button type="button" data-testid={`${testidPrefix}-add`} onClick={() => onChange([...list, ""])}
          className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase text-[#451C25] hover:text-[#2B1017]">
          <Plus size={12} /> Add source
        </button>
      </div>
      {list.length === 0 && <p className="text-[12px] text-[#6B4954] italic">No sources yet.</p>}
      <div className="space-y-2">
        {list.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <input data-testid={`${testidPrefix}-${i}`} value={s} placeholder="https://official-source.gob.es/..."
              onChange={(e) => { const n = [...list]; n[i] = e.target.value; onChange(n); }}
              className="flex-1 bg-white border border-[#451C25]/20 px-3 py-2 text-sm focus:outline-none focus:border-[#451C25]" />
            <button type="button" data-testid={`${testidPrefix}-remove-${i}`} onClick={() => onChange(list.filter((_, j) => j !== i))}
              className="text-[#6B4954] hover:text-[#2B1017]"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SeoQaPanel({ audit }) {
  const { errors, warnings } = audit;
  return (
    <div data-testid="cm-seo-qa" className="border border-[#451C25]/15 bg-white p-4">
      <div className="text-[11px] tracking-[0.22em] uppercase text-[#6B4954] mb-3">SEO & publication check</div>
      {errors.length === 0 && warnings.length === 0 ? (
        <div className="flex items-center gap-2 text-[13px] text-[#0D231B]"><CheckCircle2 size={15} /> All checks passed.</div>
      ) : (
        <div className="space-y-2">
          {errors.map((e, i) => (
            <div key={`e${i}`} data-testid={`cm-qa-error-${i}`} className="flex items-start gap-2 text-[13px] text-[#7a1d1d]">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" /> <span><strong>Blocking:</strong> {e}</span>
            </div>
          ))}
          {warnings.map((w, i) => (
            <div key={`w${i}`} data-testid={`cm-qa-warning-${i}`} className="flex items-start gap-2 text-[13px] text-[#8a6d1b]">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" /> <span>{w}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SensitiveBanner({ item }) {
  if (!isSensitivePost(item)) return null;
  const missing = [];
  if (!(item.sources || []).filter(Boolean).length) missing.push("sources");
  if (!item.last_reviewed) missing.push("last reviewed date");
  if (item.editorial_status !== "verified") missing.push("verified status");
  if (missing.length === 0) return (
    <div data-testid="cm-sensitive-ok" className="border border-[#0D231B]/20 bg-[#0D231B]/5 text-[#0D231B] px-4 py-3 text-[13px] flex items-center gap-2">
      <ShieldCheck size={15} /> Sensitive topic — sources, review date and verified status are present.
    </div>
  );
  return (
    <div data-testid="cm-sensitive-warning" className="border border-[#8a6d1b]/40 bg-[#8a6d1b]/10 text-[#6b530f] px-4 py-3 text-[13px] flex items-start gap-2">
      <AlertTriangle size={16} className="mt-0.5 shrink-0" />
      <span>Sensitive topic (immigration / tax / health / finance / legal). Missing: <strong>{missing.join(", ")}</strong>. Add them and perform a deliberate review before marking verified. The system will not auto-verify.</span>
    </div>
  );
}

/* ============================ Preview + Revisions ============================ */
export function PreviewModal({ kind, data, onClose }) {
  return (
    <div data-testid="cm-preview" className="fixed inset-0 z-[60] bg-[#451C25]/55 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-3xl bg-white border border-[#451C25]/15 my-8">
        <div className="sticky top-0 bg-[#F5EFE7] flex items-center justify-between px-6 py-4 border-b border-[#451C25]/10">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#6B4954]">Preview (not indexable · draft rendering)</div>
          <button data-testid="cm-preview-close" onClick={onClose} className="text-[#6B4954] hover:text-[#451C25]"><X size={18} /></button>
        </div>
        <article className="px-6 md:px-10 py-8">
          <h1 data-testid="cm-preview-title" className="font-serif text-3xl md:text-4xl tracking-tight text-[#451C25] mb-3">
            {kind === "post" ? (data.title || "Untitled") : (data.h1 || "Untitled")}
          </h1>
          {(data.excerpt || data.sub) && <p className="text-[#6B4954] text-lg mb-6 font-light">{data.excerpt || data.sub}</p>}
          {kind === "post" ? (
            <PostBody body={data.body} slug={data.slug || "preview"} />
          ) : (
            <div className="space-y-8">
              {(data.sections || []).filter((s) => s.h2?.trim() || s.body?.trim()).map((s, i) => (
                <section key={i}>
                  {s.h2 && <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-[#451C25] mb-2">{s.h2}</h2>}
                  <PostBody body={s.body} slug={data.slug || "preview"} />
                </section>
              ))}
              {(data.faq || []).filter((f) => f.q?.trim()).length > 0 && (
                <section>
                  <h2 className="font-serif text-2xl tracking-tight text-[#451C25] mb-3">FAQ</h2>
                  <div className="space-y-4">
                    {(data.faq || []).filter((f) => f.q?.trim()).map((f, i) => (
                      <div key={i}><p className="font-medium text-[#451C25]">{f.q}</p><p className="text-[#6B4954] font-light">{f.a}</p></div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </article>
      </div>
    </div>
  );
}

export function RevisionsModal({ axios, collection, refId, onClose, onRestored }) {
  const [revs, setRevs] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`/admin/content/revisions/${collection}/${refId}`)
      .then((r) => setRevs(r.data || []))
      .catch(() => { setRevs([]); toast.error("Could not load revisions."); });
  }, [axios, collection, refId]);
  const restore = async (id) => {
    if (!window.confirm("Restore this revision? The current version is snapshotted first.")) return;
    setBusy(true);
    try { await axios.post(`/admin/content/revisions/${id}/restore`); toast.success("Revision restored."); onRestored(); }
    catch { toast.error("Restore failed."); } finally { setBusy(false); }
  };
  return (
    <div data-testid="cm-revisions" className="fixed inset-0 z-[60] bg-[#451C25]/55 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-[#F5EFE7] border border-[#451C25]/15 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#451C25]/10">
          <h3 className="font-serif text-xl tracking-tight">Version history</h3>
          <button data-testid="cm-revisions-close" onClick={onClose} className="text-[#6B4954] hover:text-[#451C25]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-2">
          {revs === null ? <p className="text-sm text-[#6B4954]">Loading…</p>
            : revs.length === 0 ? <p className="text-sm text-[#6B4954]">No previous versions saved yet. A version is saved automatically each time you overwrite this content.</p>
            : revs.map((r) => (
              <div key={r.id} data-testid={`cm-revision-${r.id}`} className="flex items-center gap-3 bg-white border border-[#451C25]/10 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] truncate">{r.title}</div>
                  <div className="text-[11px] text-[#6B4954]">{fmtDate(r.created_at)} · {r.created_at?.slice(11, 16)}</div>
                </div>
                <button disabled={busy} data-testid={`cm-revision-restore-${r.id}`} onClick={() => restore(r.id)}
                  className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#451C25] hover:text-[#2B1017] disabled:opacity-50">
                  <RotateCcw size={12} /> Restore
                </button>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

/* ============================ Dirty-state hook ============================ */
function useDirtyGuard(dirty) {
  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);
}

/* ============================ POST EDITOR ============================ */
const EMPTY_POST = {
  slug: "", title: "", excerpt: "", body: "", cover_url: "", country_tag: "Spain", category: "Visas",
  read_time: 6, published: false, route_tags: [], seo_title: "", meta_description: "", last_reviewed: "",
  editorial_status: "", reviewed_by: "", sources: [], persona_tags: [], stage_tags: [], need_tags: [], editorial_type: "",
};

export function PostEditorCM({ axios, initial, onClose, onSaved }) {
  const seed = useMemo(() => ({ ...EMPTY_POST, ...(initial || {}) }), [initial]);
  const [data, setData] = useState(seed);
  const [baseline, setBaseline] = useState(() => JSON.stringify(seed));
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [showRevs, setShowRevs] = useState(false);
  const upd = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const isExisting = !!data.id;
  const dirty = JSON.stringify(data) !== baseline;
  useDirtyGuard(dirty);
  const audit = auditPost(data);
  const slugLocked = isExisting && initial?.published;

  const close = () => { if (dirty && !window.confirm("Discard unsaved changes?")) return; onClose(); };

  const persist = async (overrides = {}) => {
    const d = { ...data, ...overrides };
    if (d.published && auditPost(d).errors.length) { toast.error("Fix blocking issues before publishing."); return; }
    setSaving(true);
    try {
      let res;
      if (d.id) res = await axios.put(`/admin/posts/${d.id}`, d);
      else res = await axios.post(`/admin/posts`, d);
      const saved = res.data;
      const merged = { ...d, id: saved.id, editorial_status: saved.editorial_status, updated_at: saved.updated_at };
      setData(merged);
      setBaseline(JSON.stringify(merged));
      if (d.editorial_status === "verified" && saved.editorial_status !== "verified") {
        toast.info("Body/text changed — status set to review_required. Review, then set Verified again to confirm.");
      } else {
        toast.success(overrides.published === true ? "Published." : overrides.published === false ? "Unpublished." : "Saved.");
      }
      onSaved(saved);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="content-post-editor" className="fixed inset-0 z-50 bg-[#451C25]/55 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-4xl bg-[#F5EFE7] border border-[#451C25]/15 my-8">
        <header className="sticky top-0 z-10 bg-[#F5EFE7] flex items-center justify-between px-6 py-4 border-b border-[#451C25]/10">
          <div className="flex items-center gap-3">
            <h3 className="font-serif text-2xl tracking-tight">{isExisting ? "Edit article" : "New article"}</h3>
            <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${data.published ? "bg-[#0D231B]/15 text-[#0D231B]" : "bg-[#B3A17D]/30 text-[#451C25]"}`}>{data.published ? "Published" : "Draft"}</span>
            {dirty && <span data-testid="cm-dirty" className="text-[10px] tracking-[0.2em] uppercase text-[#8a6d1b]">Unsaved</span>}
          </div>
          <button data-testid="content-post-editor-close" onClick={close} className="text-[#6B4954] hover:text-[#451C25]"><X size={20} /></button>
        </header>

        <div className="p-6 space-y-6">
          <SensitiveBanner item={data} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Txt label="Title *" value={data.title} testid="cm-post-title"
              on={(v) => setData((d) => ({ ...d, title: v, slug: (!d.id && (!d.slug || d.slug === slugify(d.title))) ? slugify(v) : d.slug }))} />
            <Txt label="Slug *" value={data.slug} testid="cm-post-slug" disabled={slugLocked}
              hint={slugLocked ? "Locked — slug can't change on a published article (protects its indexed URL). Unpublish to change." : "Public URL: /blog/" + (data.slug || "…")}
              on={(v) => upd("slug", v)} />
          </div>
          <Area label="Excerpt *" value={data.excerpt} on={(v) => upd("excerpt", v)} testid="cm-post-excerpt" rows={2} />

          <div>
            <label className={labelCls}>Body * (Markdown — headings, lists, links, callouts)</label>
            <div data-testid="cm-post-body" data-color-mode="light">
              <MDEditor value={data.body} onChange={(v) => upd("body", v || "")} height={460} preview="live" visibleDragbar={false} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Txt label="SEO title (fallback: title)" value={data.seo_title} testid="cm-post-seo-title" on={(v) => upd("seo_title", v)} hint={`${(data.seo_title || "").length} chars`} />
            <Txt label="Cover image URL" value={data.cover_url} testid="cm-post-cover" placeholder="https://… (existing asset)" on={(v) => upd("cover_url", v)} />
          </div>
          <Area label="Meta description (fallback: excerpt)" value={data.meta_description} testid="cm-post-meta" rows={2} on={(v) => upd("meta_description", v)} hint={`${(data.meta_description || "").length} chars (ideal 80-160)`} />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Sel label="Country" value={data.country_tag} testid="cm-post-country" on={(v) => upd("country_tag", v)}
              options={["All", "Spain", "Portugal", "Italy"].map((c) => ({ v: c, l: c }))} />
            <Sel label="Category" value={data.category} testid="cm-post-category" on={(v) => upd("category", v)}
              options={POST_CATEGORIES.map((c) => ({ v: c, l: c }))} />
            <Txt label="Read time (min)" value={String(data.read_time || 6)} testid="cm-post-readtime" on={(v) => upd("read_time", parseInt(v, 10) || 6)} />
          </div>

          <div>
            <label className={labelCls}>Route classification</label>
            <div className="flex flex-wrap gap-2">
              {ROUTE_TAGS.map((t) => {
                const on = (data.route_tags || []).includes(t);
                return (
                  <button key={t} type="button" data-testid={`cm-post-route-${t}`} aria-pressed={on}
                    onClick={() => upd("route_tags", on ? data.route_tags.filter((x) => x !== t) : [...(data.route_tags || []), t])}
                    className={`text-[11px] tracking-[0.16em] uppercase px-3 py-1.5 border ${on ? "bg-[#451C25] text-white border-[#451C25]" : "border-[#451C25]/30 text-[#451C25]"}`}>{t}</button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Sel label="Editorial status" value={data.editorial_status || ""} testid="cm-post-editorial-status" on={(v) => upd("editorial_status", v)}
              options={[{ v: "", l: "— unset —" }, { v: "review", l: "Review (figures pending)" }, { v: "review_required", l: "Review required" }, { v: "verified", l: "Verified (checked vs sources)" }]} />
            <Txt label="Last reviewed (YYYY-MM-DD)" value={data.last_reviewed} testid="cm-post-last-reviewed" on={(v) => upd("last_reviewed", v)} />
          </div>

          <SourcesEditor sources={data.sources} onChange={(v) => upd("sources", v)} testidPrefix="cm-source" />

          <SeoQaPanel audit={audit} />

          <label className="flex items-center gap-3 text-sm text-[#451C25]">
            <input data-testid="cm-post-published-toggle" type="checkbox" checked={data.published} onChange={(e) => upd("published", e.target.checked)} className="accent-[#451C25]" />
            Published (visible at /blog & in sitemap)
          </label>
        </div>

        <footer className="sticky bottom-0 bg-[#F5EFE7] border-t border-[#451C25]/10 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button data-testid="cm-preview-open" onClick={() => setPreview(true)} className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.2em] uppercase border border-[#451C25]/30 hover:bg-[#451C25] hover:text-white"><Eye size={13} /> Preview</button>
            {isExisting && <button data-testid="cm-revisions-open" onClick={() => setShowRevs(true)} className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.2em] uppercase border border-[#451C25]/30 hover:bg-[#451C25] hover:text-white"><History size={13} /> History</button>}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="cm-save" disabled={saving} onClick={() => persist()} className="inline-flex items-center gap-1.5 px-4 py-2 text-[11px] tracking-[0.2em] uppercase border border-[#451C25]/30 hover:bg-[#451C25] hover:text-white disabled:opacity-50">{saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save</button>
            {data.published
              ? <button data-testid="cm-unpublish" disabled={saving} onClick={() => persist({ published: false })} className="inline-flex items-center gap-1.5 px-4 py-2 text-[11px] tracking-[0.2em] uppercase border border-[#451C25]/30 hover:bg-[#451C25] hover:text-white disabled:opacity-50"><EyeOff size={13} /> Unpublish</button>
              : <button data-testid="cm-publish" disabled={saving || audit.errors.length > 0} title={audit.errors.length ? "Resolve blocking issues first" : ""} onClick={() => persist({ published: true })} className="inline-flex items-center gap-1.5 bg-[#451C25] text-white px-5 py-2 text-[11px] tracking-[0.2em] uppercase hover:bg-[#2B1017] disabled:opacity-50"><Send size={13} /> Publish</button>}
          </div>
        </footer>
      </div>
      {preview && <PreviewModal kind="post" data={data} onClose={() => setPreview(false)} />}
      {showRevs && <RevisionsModal axios={axios} collection="posts" refId={data.id} onClose={() => setShowRevs(false)} onRestored={() => { setShowRevs(false); onSaved({ id: data.id }); onClose(); }} />}
    </div>
  );
}

/* ============================ SEO PAGE EDITOR ============================ */
export function SeoEditorCM({ axios, initial, onClose, onSaved }) {
  const seed = useMemo(() => ({
    slug: initial.slug, intent: initial.intent, country: initial.country, meta_title: initial.meta_title || "",
    meta_description: initial.meta_description || "", og_image: initial.og_image || "", h1: initial.h1 || "", sub: initial.sub || "",
    schema_type: initial.schema_type || "WebPage", editorial_status: initial.editorial_status || "", last_reviewed: initial.last_reviewed || "",
    reviewed_by: initial.reviewed_by || "", route_tags: initial.route_tags || [], sources: initial.sources || [],
    target_keywords: initial.target_keywords || [], sections: (initial.sections || []).map((s) => ({ ...s })),
    faq: (initial.faq || []).map((f) => ({ ...f })), published: !!initial.published,
  }), [initial]);
  const [data, setData] = useState(seed);
  const [baseline, setBaseline] = useState(() => JSON.stringify(seed));
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [showRevs, setShowRevs] = useState(false);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const dirty = JSON.stringify(data) !== baseline;
  useDirtyGuard(dirty);
  const audit = auditSeo(data);
  const close = () => { if (dirty && !window.confirm("Discard unsaved changes?")) return; onClose(); };

  const setSection = (i, k, v) => setData((d) => { const n = [...d.sections]; n[i] = { ...n[i], [k]: v }; return { ...d, sections: n }; });
  const setFaq = (i, k, v) => setData((d) => { const n = [...d.faq]; n[i] = { ...n[i], [k]: v }; return { ...d, faq: n }; });

  const persist = async (overrides = {}) => {
    const d = { ...data, ...overrides };
    if (d.published && auditSeo(d).errors.length) { toast.error("Fix blocking issues before publishing."); return; }
    const payload = {
      slug: d.slug, intent: d.intent, country: d.country, meta_title: d.meta_title.trim(),
      meta_description: d.meta_description.trim(), og_image: d.og_image.trim() || null, h1: d.h1.trim(), sub: d.sub.trim(),
      schema_type: d.schema_type || null, editorial_status: d.editorial_status || null, last_reviewed: d.last_reviewed || null,
      reviewed_by: d.reviewed_by || null, route_tags: d.route_tags || [],
      sources: (d.sources || []).filter(Boolean),
      target_keywords: Array.isArray(d.target_keywords) ? d.target_keywords : String(d.target_keywords).split(",").map((s) => s.trim()).filter(Boolean),
      sections: d.sections.filter((s) => s.h2?.trim() && s.body?.trim()).map((s) => ({ h2: s.h2.trim(), body: s.body })),
      faq: d.faq.filter((f) => f.q?.trim() && f.a?.trim()).map((f) => ({ q: f.q.trim(), a: f.a })),
      published: d.published,
    };
    setSaving(true);
    try {
      const res = await axios.put(`/admin/seo-pages/${d.slug}`, payload);
      const saved = res.data;
      setData((prev) => ({ ...prev, ...overrides, editorial_status: saved.editorial_status }));
      setBaseline(JSON.stringify({ ...d, editorial_status: saved.editorial_status }));
      if (d.editorial_status === "verified" && saved.editorial_status !== "verified") toast.info("Content changed — status set to review_required. Re-verify when reviewed.");
      else toast.success(overrides.published === true ? "Published." : overrides.published === false ? "Unpublished." : "Saved.");
      onSaved(saved);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Save failed.");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="content-seo-editor" className="fixed inset-0 z-50 bg-[#451C25]/55 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-4xl bg-[#F5EFE7] border border-[#451C25]/15 my-8">
        <header className="sticky top-0 z-10 bg-[#F5EFE7] flex items-center justify-between px-6 py-4 border-b border-[#451C25]/10">
          <div className="flex items-center gap-3">
            <h3 className="font-serif text-2xl tracking-tight">Edit SEO page</h3>
            <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${data.published ? "bg-[#0D231B]/15 text-[#0D231B]" : "bg-[#B3A17D]/30 text-[#451C25]"}`}>{data.published ? "Published" : "Draft"}</span>
            {dirty && <span data-testid="cm-dirty" className="text-[10px] tracking-[0.2em] uppercase text-[#8a6d1b]">Unsaved</span>}
          </div>
          <button data-testid="content-seo-editor-close" onClick={close} className="text-[#6B4954] hover:text-[#451C25]"><X size={20} /></button>
        </header>

        <div className="p-6 space-y-6">
          <SensitiveBanner item={{ ...data, title: data.h1 }} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Txt label="Slug (locked)" value={data.slug} testid="cm-seo-slug" disabled />
            <Sel label="Intent" value={data.intent} testid="cm-seo-intent" on={(v) => set("intent", v)} options={[{ v: "retire", l: "Retire" }, { v: "relocate", l: "Relocate" }]} />
            <Sel label="Country" value={data.country} testid="cm-seo-country" on={(v) => set("country", v)} options={["Spain", "Portugal", "Italy"].map((c) => ({ v: c, l: c }))} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Txt label="H1 *" value={data.h1} testid="cm-seo-h1" on={(v) => set("h1", v)} />
            <Sel label="Schema type" value={data.schema_type} testid="cm-seo-schema-type" on={(v) => set("schema_type", v)} options={[{ v: "WebPage", l: "WebPage" }, { v: "Article", l: "Article (editorial guide)" }]} />
          </div>
          <Txt label="Meta title *" value={data.meta_title} testid="cm-seo-metatitle" on={(v) => set("meta_title", v)} hint={`${data.meta_title.length} chars (ideal 55-65)`} />
          <Area label="Meta description" value={data.meta_description} testid="cm-seo-metadesc" rows={2} on={(v) => set("meta_description", v)} hint={`${data.meta_description.length} chars (ideal 150-160)`} />
          <Area label="Sub (hero subtitle)" value={data.sub} testid="cm-seo-sub" rows={2} on={(v) => set("sub", v)} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Sel label="Editorial status" value={data.editorial_status || ""} testid="cm-seo-editorial-status" on={(v) => set("editorial_status", v)}
              options={[{ v: "", l: "— unset —" }, { v: "review", l: "Review" }, { v: "review_required", l: "Review required" }, { v: "verified", l: "Verified" }]} />
            <Txt label="Last reviewed (YYYY-MM-DD)" value={data.last_reviewed} testid="cm-seo-last-reviewed" on={(v) => set("last_reviewed", v)} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className={labelCls} style={{ marginBottom: 0 }}>Sections ({data.sections.length})</label>
              <button type="button" data-testid="cm-seo-add-section" onClick={() => set("sections", [...data.sections, { h2: "", body: "" }])} className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#451C25]"><Plus size={12} /> Add section</button>
            </div>
            <div className="space-y-4">
              {data.sections.map((s, i) => (
                <div key={i} data-testid={`cm-seo-section-${i}`} className="bg-white border border-[#451C25]/10 p-4 space-y-2">
                  <div className="flex items-center justify-between"><span className="text-[10px] tracking-[0.2em] uppercase text-[#6B4954]">Section {i + 1}</span>
                    <button onClick={() => set("sections", data.sections.filter((_, j) => j !== i))} data-testid={`cm-seo-remove-section-${i}`} className="text-[#6B4954] hover:text-[#2B1017]"><Trash2 size={13} /></button></div>
                  <input data-testid={`cm-seo-section-${i}-h2`} value={s.h2} onChange={(e) => setSection(i, "h2", e.target.value)} placeholder="H2 title" className="w-full bg-[#F5EFE7] border border-[#451C25]/15 px-3 py-2 text-sm font-serif focus:outline-none focus:border-[#451C25]" />
                  <textarea data-testid={`cm-seo-section-${i}-body`} value={s.body} onChange={(e) => setSection(i, "body", e.target.value)} rows={6} placeholder="Body (markdown)" className="w-full bg-[#F5EFE7] border border-[#451C25]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#451C25]" />
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className={labelCls} style={{ marginBottom: 0 }}>FAQ ({data.faq.length})</label>
              <button type="button" data-testid="cm-seo-add-faq" onClick={() => set("faq", [...data.faq, { q: "", a: "" }])} className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[#451C25]"><Plus size={12} /> Add FAQ</button>
            </div>
            <div className="space-y-3">
              {data.faq.map((f, i) => (
                <div key={i} data-testid={`cm-seo-faq-${i}`} className="bg-white border border-[#451C25]/10 p-4 space-y-2">
                  <div className="flex items-center justify-between"><span className="text-[10px] tracking-[0.2em] uppercase text-[#6B4954]">Q {i + 1}</span>
                    <button onClick={() => set("faq", data.faq.filter((_, j) => j !== i))} data-testid={`cm-seo-remove-faq-${i}`} className="text-[#6B4954] hover:text-[#2B1017]"><Trash2 size={13} /></button></div>
                  <input data-testid={`cm-seo-faq-${i}-q`} value={f.q} onChange={(e) => setFaq(i, "q", e.target.value)} placeholder="Question" className="w-full bg-[#F5EFE7] border border-[#451C25]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#451C25]" />
                  <textarea data-testid={`cm-seo-faq-${i}-a`} value={f.a} onChange={(e) => setFaq(i, "a", e.target.value)} rows={3} placeholder="Answer" className="w-full bg-[#F5EFE7] border border-[#451C25]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#451C25]" />
                </div>
              ))}
            </div>
          </div>

          <SourcesEditor sources={data.sources} onChange={(v) => set("sources", v)} testidPrefix="cm-seo-source" />
          <SeoQaPanel audit={audit} />
        </div>

        <footer className="sticky bottom-0 bg-[#F5EFE7] border-t border-[#451C25]/10 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button data-testid="cm-seo-preview" onClick={() => setPreview(true)} className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.2em] uppercase border border-[#451C25]/30 hover:bg-[#451C25] hover:text-white"><Eye size={13} /> Preview</button>
            <button data-testid="cm-seo-revisions-open" onClick={() => setShowRevs(true)} className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.2em] uppercase border border-[#451C25]/30 hover:bg-[#451C25] hover:text-white"><History size={13} /> History</button>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="cm-seo-save" disabled={saving} onClick={() => persist()} className="inline-flex items-center gap-1.5 px-4 py-2 text-[11px] tracking-[0.2em] uppercase border border-[#451C25]/30 hover:bg-[#451C25] hover:text-white disabled:opacity-50">{saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save</button>
            {data.published
              ? <button data-testid="cm-seo-unpublish" disabled={saving} onClick={() => persist({ published: false })} className="inline-flex items-center gap-1.5 px-4 py-2 text-[11px] tracking-[0.2em] uppercase border border-[#451C25]/30 hover:bg-[#451C25] hover:text-white disabled:opacity-50"><EyeOff size={13} /> Unpublish</button>
              : <button data-testid="cm-seo-publish" disabled={saving || audit.errors.length > 0} onClick={() => persist({ published: true })} className="inline-flex items-center gap-1.5 bg-[#451C25] text-white px-5 py-2 text-[11px] tracking-[0.2em] uppercase hover:bg-[#2B1017] disabled:opacity-50"><Send size={13} /> Publish</button>}
          </div>
        </footer>
      </div>
      {preview && <PreviewModal kind="seo" data={data} onClose={() => setPreview(false)} />}
      {showRevs && <RevisionsModal axios={axios} collection="seo_pages" refId={data.slug} onClose={() => setShowRevs(false)} onRestored={() => { setShowRevs(false); onSaved({ slug: data.slug }); onClose(); }} />}
    </div>
  );
}
